import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DashboardSkeleton } from '../components/PageSkeletons';
import { PullToRefresh } from '../components/PullToRefresh';
import axios from 'axios';
import {
  Trophy,
  Users,
  Calendar,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  Flag,
  BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      const statsRes = await axios.get(`${API}/stats/dashboard`);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchDashboardData}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            {t('dashboard.welcome') || 'WELCOME'}, <span className="text-primary">{user?.name?.toUpperCase()}</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('dashboard.subtitle') || "Here's what's happening with your tournaments"}
          </p>
        </div>
        
        <div className="flex gap-3 mt-4 md:mt-0">
          {(user?.role === 'admin' || user?.role === 'organizer') && (
            <Link to="/tournaments/create">
              <Button className="btn-primary gap-2" data-testid="create-tournament-btn">
                <Plus className="w-4 h-4" />
                {t('tournament.createTournament')}
              </Button>
            </Link>
          )}
          <Link to="/teams/create">
            <Button variant="outline" className="gap-2" data-testid="create-team-btn">
              <Users className="w-4 h-4" />
              {t('team.createTeam')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <Card className="mb-8 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5" data-testid="quick-actions-card">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-lg font-semibold">Team Performance</p>
                <p className="text-sm text-muted-foreground">View stats, compare teams, and track player performance</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/performance">
                <Button variant="outline" className="gap-2" data-testid="view-stats-btn">
                  View Stats
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/compare">
                <Button className="btn-primary gap-2" data-testid="compare-teams-btn">
                  Compare Teams
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger-fade-in">
        <Card className="card-hover" data-testid="stat-tournaments">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Tournaments</p>
                <p className="text-3xl font-black mt-1">{stats?.total_tournaments || 0}</p>
              </div>
              <Trophy className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover" data-testid="stat-teams">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Registered Teams</p>
                <p className="text-3xl font-black mt-1">{stats?.total_teams || 0}</p>
              </div>
              <Users className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover" data-testid="stat-games">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Games Played</p>
                <p className="text-3xl font-black mt-1">{stats?.total_games || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-accent" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover" data-testid="stat-active">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Tournaments</p>
                <p className="text-3xl font-black mt-1">{stats?.active_tournaments || 0}</p>
              </div>
              <Flag className="w-8 h-8 text-accent" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Games */}
        <Card data-testid="upcoming-games-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Upcoming Games
            </CardTitle>
            <Link to="/games">
              <Button variant="ghost" size="sm" className="gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.upcoming_games?.length > 0 ? (
              <div className="space-y-3">
                {stats.upcoming_games.map((game, index) => (
                  <div 
                    key={game.id || index}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    data-testid={`upcoming-game-${index}`}
                  >
                    <div className="flex-1">
                      <p className="font-semibold">
                        {game.team1_name} vs {game.team2_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {game.field} • {game.round}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono">{formatDate(game.scheduled_time)}</p>
                      <Badge variant="outline" className="mt-1">{game.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No upcoming games scheduled</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Results */}
        <Card data-testid="recent-results-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              Recent Results
            </CardTitle>
            <Link to="/games">
              <Button variant="ghost" size="sm" className="gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_results?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_results.map((game, index) => (
                  <div 
                    key={game.id || index}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                    data-testid={`recent-result-${index}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={game.winner_id === game.team1_id ? 'font-bold text-accent' : ''}>
                          {game.team1_name}
                        </span>
                        <span className="font-mono text-lg font-bold">
                          {game.team1_score} - {game.team2_score}
                        </span>
                        <span className={game.winner_id === game.team2_id ? 'font-bold text-accent' : ''}>
                          {game.team2_name}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{game.round}</p>
                    </div>
                    <Badge variant="secondary">Final</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No recent results</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="mt-8" data-testid="quick-actions-card">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/tournaments" className="block">
              <div className="p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors text-center">
                <Trophy className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-medium">Browse Tournaments</p>
              </div>
            </Link>
            <Link to="/compare" className="block">
              <div className="p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors text-center">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 text-accent" />
                <p className="font-medium">Compare Teams</p>
              </div>
            </Link>
            <Link to="/predictions" className="block">
              <div className="p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors text-center">
                <Flag className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-medium">AI Predictions</p>
              </div>
            </Link>
            <Link to="/leaderboard" className="block">
              <div className="p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-accent" />
                <p className="font-medium">Leaderboard</p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
    </PullToRefresh>
  );
};
